import { EventDetailOutputDto, FilterTransactionByPaidEventIdRequestParams } from '@smatchy/sdk';
import type { TransactionAWalletShows } from '../../../../presentations/pages/logged/pages/home/pages/profile-page/pages/my-wallet/domain/the-transaction-a-wallet-shows';
import { WhatWentWrongWithThePayment } from '../domain/what-went-wrong-with-the-payment';

export interface PaymentMethod {
  id: string;
  type: 'stripe' | 'apple_pay' | 'google_pay' | 'paypal' | 'visa' | 'mastercard' | 'amex' | 'discover';
  name: string;
  last4?: string;
  brand?: string;
  displayBrand?: string;
  isDefault: boolean;
}

export interface PromoCode {
  id: string;
  code: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  maxUses: number;
  expirationDate: Date;
}
export interface TransactionHistoryResponse {
  data: {
    // The wallet's own type, named by #515, and what the transaction card
    // declares as its input. PayoutHistoryItemDto is the API's shape, which
    // is not the same thing.
    transactions: TransactionAWalletShows[];
    date: string;
  }[];
  total: number;
  page: number;
  limit: number;
}
export class SelectPaymentMethod {
  static readonly type = '[Payment] SelectPaymentMethod';
  /**
   * null means «no card selected any more», which is what deleting the
   * selected one leaves behind. The state has always declared
   * `PaymentMethod | null`; only this action said otherwise, and the screen
   * bridged the gap with `null as any`.
   */
  constructor(public payload: PaymentMethod | null) {}
}

export class ApplyPromoCode {
  static readonly type = '[Payment] ApplyPromoCode';
  constructor(public payload: PromoCode) {}
}

export class RemovePromoCode {
  static readonly type = '[Payment] RemovePromoCode';
}

export class ProcessPayment {
  static readonly type = '[Payment] ProcessPayment';
  constructor(
    public payload: {
      eventId: string;
      paymentMethodId?: string; // Optional: if not provided, Stripe will collect new card details
      promoCodeId?: string;
    },
  ) {}
}

export class PayWithBalance {
  static readonly type = '[Payment] PayWithBalance';
  constructor(public payload: { eventId: string }) {}
}

export class PaymentSuccess {
  static readonly type = '[Payment] PaymentSuccess';
  constructor(public payload: { transactionId: string }) {}
}

export class PaymentError {
  static readonly type = '[Payment] PaymentError';
  constructor(
    public payload: {
      error: string;
      /**
       * Left out means ours. Nineteen places dispatch this action, and a
       * failure nobody classified is more likely to be a fault of ours than a
       * card being refused, so the silence defaults to the side that rings.
       */
      kind?: WhatWentWrongWithThePayment;
      /** Where in the payment it died, for grouping. */
      stage?: string;
    },
  ) {}
}

export class SetActivityData {
  static readonly type = '[Payment] SetActivityData';
  constructor(public payload: EventDetailOutputDto) {}
}

export class ResetPaymentState {
  static readonly type = '[Payment] ResetPaymentState';
}

export class LoadTransactionHistory {
  static readonly type = '[Payment] LoadTransactionHistory';
  constructor(
    public payload: {
      eventId?: string;
      status?: 'pending' | 'completed' | 'failed' | 'refunded';
    },
  ) {}
}

export class FilterTransactionAction {
  static readonly type = '[Payment] Filter transaction';
  // The state hands this straight to filterTransactionByPaidEventId, which
  // accepts nothing else.
  constructor(public filter: FilterTransactionByPaidEventIdRequestParams) {}
}
export class FilterTransactionSuccessAction {
  static readonly type = '[Payment] Filter transaction success';
  constructor(public response: TransactionHistoryResponse) {}
}

export class AddDateFilterToFilterTransactionHistoryAction {
  static readonly type = '[FilterEvents] Add Date Filter To Filter Transaction History Action';

  constructor(public dateFilter: string, public fromDate?: string, public toDate?: string) {}
}

export class RemoveDateFilterToFilterTransactionHistoryAction {
  static readonly type = '[FilterEvents] RemoveDate Filter To Filter Transaction History Action';
}

export class AddPriceToFilterTransactionHistoriesAction {
  static readonly type = '[FilterEvents] AddPrice To Filter Transaction Histories Action';

  constructor(public priceFrom: number, public priceTo: number) {}
}

export class RemovePriceToFilterTransactionHistoriesAction {
  static readonly type = '[FilterEvents] Remove Price To Filter Transaction Histories Action';
}

export class AddStatusToFilterTransactionHistoryAction {
  static readonly type = '[FilterEvents] Add Status To Filter Transaction History Action';

  constructor(public status: string) {}
}
export class RemoveStatusToFilterTransactionHistoryAction {
  static readonly type = '[FilterEvents] Remove Status To Filter Transaction History Action';

  constructor(public status: string) {}
}
